// The company's letterhead — what sits at the top of every form PDF.
//
// Round 29. A submitted form is what a customer emails to a principal
// contractor or hands to an auditor, so the document says who issued it. This
// screen is where they say so: the contact details, and a logo if they have
// one.
//
// EVERYTHING HERE IS OPTIONAL, and the page says so rather than implying it
// with an asterisk. A company that fills in none of it gets exactly the
// document they get today — their name, in text, at the top. There is no empty
// box waiting to be filled, which was the objection that deferred the logo in
// round 16 and is the reason it could be built now.
//
// The ABN is deliberately NOT editable here. It lives on the billing page,
// beside the tax documents it is printed on (round 19), and one field with two
// doors is two things to keep in step for no gain — so this page shows it and
// points at the door.
import { useState } from 'react';
import { FileDrop } from '../components/ui/FileDrop';
import { exampleNumber as renderExample, formatOptions, type NumberFormat } from '../lib/numbering';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../api';
import { useSession } from '../session';
import { Banner, Field } from '../ui';

/** What the upload gate accepts. Narrower than a site photo, deliberately. */
const LOGO_ACCEPT = 'image/png,image/jpeg,.png,.jpg,.jpeg';

export function CompanySettingsPage() {
  const { tenant, refresh } = useSession();

  if (!tenant) return null;

  return (
    <section>
      <h1>Company details</h1>
      <p className="hint">
        The contact details and logo appear at the top of every form your crew submits, so the
        PDF says who issued it. All of that is optional; anything you leave blank is simply
        left off the page.
      </p>

      <CompanyNamePanel onSaved={refresh} />
      <DetailsPanel onSaved={refresh} />
      <LogoPanel onSaved={refresh} />

      <section className="panel">
        <h2>Your ABN</h2>
        <p>{tenant.abn ?? 'Not given.'}</p>
        <p className="hint">
          Changed on the <Link to="/app/settings/billing">billing page</Link>, where it sits
          beside the tax documents it is printed on.
        </p>
      </section>

      <TimezonePanel onSaved={refresh} />
      <JobNumberingPanel onSaved={refresh} />
    </section>
  );
}

type JobNumberFormat = NumberFormat;

/** An example only; the server allocates the real number, skipping any taken. */
function exampleNumber(format: JobNumberFormat, prefix: string, counter: number): string {
  return renderExample(format, prefix || 'JOB', counter);
}

const FORMAT_OPTIONS = formatOptions('JOB');

/**
 * How job numbers look (round 36). Three formats, the prefix when it applies,
 * and the next number — the last one so a company can carry on from whatever
 * their old system was up to, which is the first thing a switcher asks.
 */
function JobNumberingPanel({ onSaved }: { onSaved: () => Promise<void> }) {
  const { tenant } = useSession();
  const [format, setFormat] = useState<JobNumberFormat>(tenant?.jobNumberFormat ?? 'prefixed');
  const [prefix, setPrefix] = useState(tenant?.jobNumberPrefix ?? 'JOB');
  const [next, setNext] = useState(String(tenant?.nextJobNumber ?? 1));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  if (!tenant) return null;

  const dirty =
    format !== tenant.jobNumberFormat ||
    prefix !== tenant.jobNumberPrefix ||
    next !== String(tenant.nextJobNumber);

  // The counter the example shows. Under 'yearly' the allocator restarts at 1
  // when the stamped year is not this year — so switching TO yearly shows
  // 001, found in the browser when the example read 2026-004 while the
  // allocator, rightly, handed out 2026-001. A next number the person TYPES
  // wins even then, because saving it anchors the yearly count to this year
  // (the server stamps the year alongside it).
  const exampleCounter =
    format === 'yearly' &&
    tenant.jobNumberYear !== new Date().getFullYear() &&
    next === String(tenant.nextJobNumber)
      ? 1
      : Number(next) || 1;

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFieldErrors({});
    setSaved(false);
    try {
      await api.updateCompany({
        jobNumberFormat: format,
        // The prefix only matters under 'prefixed', but it is sent whenever
        // it changed so switching format back later finds what was typed.
        jobNumberPrefix: prefix,
        // Only when it was changed: an untouched next number under 'yearly'
        // anchored the count to this year and cancelled the January restart
        // (found reviewing the quote panel, its twin, 2026-10-04).
        ...(next !== String(tenant?.nextJobNumber) ? { nextJobNumber: Number(next) } : {}),
      });
      await onSaved();
      setSaved(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFieldErrors(err.fields);
      } else {
        setError('Could not save that. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>Job numbering</h2>
      <form onSubmit={save} noValidate aria-label="Job numbering">
        {error && <Banner kind="error">{error}</Banner>}
        {saved && !dirty && <Banner kind="ok">Saved.</Banner>}
        <div>
          <label className="form-label" htmlFor="job-number-format">
            Format
          </label>
          <select
            id="job-number-format"
            className="form-select"
            value={format}
            onChange={(e) => setFormat(e.target.value as JobNumberFormat)}
          >
            {FORMAT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="row">
          {format === 'prefixed' && (
            <Field
              label="Prefix"
              name="jobNumberPrefix"
              value={prefix}
              onChange={(value) => setPrefix(value.toUpperCase())}
              error={fieldErrors.jobNumberPrefix}
              autoCapitalize="characters"
              spellCheck={false}
            />
          )}
          <Field
            label="Next number"
            name="nextJobNumber"
            value={next}
            onChange={setNext}
            error={fieldErrors.nextJobNumber}
            inputMode="numeric"
          />
        </div>
        <p className="hint">
          The next job created will be{' '}
          <strong>{exampleNumber(format, prefix, exampleCounter)}</strong>. Set the next
          number to carry on from a previous system; numbers already used are skipped, never
          reused. Jobs that exist keep the numbers they have, and any job&rsquo;s number can be
          edited on its own page.
        </p>
        <div className="row">
          <button type="submit" disabled={busy || !dirty}>
            Save
          </button>
        </div>
      </form>
    </section>
  );
}

/**
 * The Australian zones, with what each means in plain words. The value stored
 * is the IANA name, which is what the server validates and what keeps daylight
 * saving automatic — the picker is curated, the server is not, so a customer
 * over a border someday is a list entry, not a schema change.
 */
const TIMEZONES: { value: string; label: string }[] = [
  { value: 'Australia/Sydney', label: 'Sydney / Canberra (AEST, daylight saving)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST, daylight saving)' },
  { value: 'Australia/Hobart', label: 'Hobart (AEST, daylight saving)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST, no daylight saving)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACST, daylight saving)' },
  { value: 'Australia/Broken_Hill', label: 'Broken Hill (ACST, daylight saving)' },
  { value: 'Australia/Darwin', label: 'Darwin (ACST, no daylight saving)' },
  { value: 'Australia/Perth', label: 'Perth (AWST, no daylight saving)' },
  { value: 'Australia/Eucla', label: 'Eucla (ACWST, no daylight saving)' },
  { value: 'Australia/Lord_Howe', label: 'Lord Howe Island (daylight saving)' },
];

function TimezonePanel({ onSaved }: { onSaved: () => Promise<void> }) {
  const { tenant } = useSession();
  const [timezone, setTimezone] = useState(tenant?.timezone ?? 'Australia/Sydney');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  if (!tenant) return null;

  // A zone set some other way (the signup API accepts any real IANA name)
  // must still show as itself rather than snapping to the first option.
  const options = TIMEZONES.some((z) => z.value === timezone)
    ? TIMEZONES
    : [{ value: timezone, label: timezone }, ...TIMEZONES];

  const dirty = timezone !== tenant.timezone;

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      // Only the timezone — a missing key means "unchanged" on this PATCH,
      // which is what lets each panel on this page save its own fields.
      await api.updateCompany({ timezone });
      await onSaved();
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>Timezone</h2>
      <form onSubmit={save} noValidate aria-label="Timezone">
        {error && <Banner kind="error">{error}</Banner>}
        {saved && !dirty && <Banner kind="ok">Saved.</Banner>}
        <div>
          <label className="form-label" htmlFor="company-timezone">
            Where your company keeps time
          </label>
          <select
            id="company-timezone"
            className="form-select"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          >
            {options.map((zone) => (
              <option key={zone.value} value={zone.value}>
                {zone.label}
              </option>
            ))}
          </select>
        </div>
        <p className="hint">
          This decides when your day ticks over: expiry and service reminders, the timestamps on
          submitted forms and emails, and your billing month. Daylight saving is handled
          automatically. Documents already rendered keep the time they were made with.
        </p>
        <div className="row">
          <button type="submit" disabled={busy || !dirty}>
            Save
          </button>
        </div>
      </form>
    </section>
  );
}

/**
 * The company's own name, which was set at signup and — until 2026-09-16 —
 * changeable by nobody: no screen sent it and the server refused it. The
 * missing-door shape again (the ABN this page's router was born for, the
 * client edit of round 33, the display name of 2026-09-14), found by Nathan
 * renaming HIMSELF and then looking for the company that is named after him.
 *
 * Seeded with the current name and disabled until changed, the Security
 * page's "Your name" panel shape. The PATCH carries the name and NOTHING
 * else — the server reads a missing key as "unchanged", and a body that also
 * sent the letterhead fields blank would wipe them saving a spelling fix.
 */
function CompanyNamePanel({ onSaved }: { onSaved: () => Promise<void> }) {
  const { tenant } = useSession();
  const [name, setName] = useState(tenant?.name ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  if (!tenant) return null;

  const dirty = name.trim() !== tenant.name;

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFieldErrors({});
    setSaved(false);
    try {
      await api.updateCompany({ name });
      await onSaved();
      setSaved(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFieldErrors(err.fields);
      } else {
        setError('Could not save that. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>Company name</h2>
      <form onSubmit={save} noValidate aria-label="Company name">
        {error && <Banner kind="error">{error}</Banner>}
        {saved && !dirty && <Banner kind="ok">Saved.</Banner>}
        <div className="row">
          <Field
            label="Name"
            name="companyName"
            value={name}
            onChange={setName}
            error={fieldErrors.name}
            autoComplete="organization"
          />
          <button type="submit" disabled={busy || !dirty}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
      <p className="hint">
        How your company appears across Dayboard: the sidebar, the letterhead on form PDFs,
        and the tax invoices we issue you from now on. Invoices already issued keep the name
        they were issued under.
      </p>
    </section>
  );
}

function DetailsPanel({ onSaved }: { onSaved: () => Promise<void> }) {
  const { tenant } = useSession();
  const [address, setAddress] = useState(tenant?.address ?? '');
  const [phone, setPhone] = useState(tenant?.phone ?? '');
  const [email, setEmail] = useState(tenant?.email ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  if (!tenant) return null;

  const dirty =
    address !== (tenant.address ?? '') ||
    phone !== (tenant.phone ?? '') ||
    email !== (tenant.email ?? '');

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFieldErrors({});
    setSaved(false);
    try {
      // THE THREE FIELDS THIS PANEL OWNS, and nothing else. The ABN is not
      // sent: a form that cannot show a field must OMIT it rather than send
      // its empty value, or saving a phone number here would quietly clear an
      // ABN off the company's tax invoices.
      await api.updateCompany({ address, phone, email });
      await onSaved();
      setSaved(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFieldErrors(err.fields);
      } else {
        setError('Could not save that. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>Contact details</h2>
      {/* Named because the page now holds two forms (round 30 added the
          timezone), and "the Save button" stopped being one thing. */}
      <form onSubmit={save} noValidate aria-label="Contact details">
        {error && <Banner kind="error">{error}</Banner>}
        {saved && !dirty && <Banner kind="ok">Saved.</Banner>}
        <Field
          label="Address"
          name="address"
          value={address}
          onChange={setAddress}
          error={fieldErrors.address}
          required={false}
          hint="One line is plenty; it prints on the right of the letterhead."
          autoComplete="street-address"
        />
        <Field
          label="Phone"
          name="phone"
          value={phone}
          onChange={setPhone}
          error={fieldErrors.phone}
          required={false}
          autoComplete="tel"
          inputMode="tel"
        />
        <Field
          label="Email"
          name="email"
          type="email"
          value={email}
          onChange={setEmail}
          error={fieldErrors.email}
          required={false}
          hint="The address you would want a principal contractor to reply to."
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          inputMode="email"
        />
        <div className="row">
          <button type="submit" disabled={busy || !dirty}>
            Save
          </button>
        </div>
      </form>
    </section>
  );
}

function LogoPanel({ onSaved }: { onSaved: () => Promise<void> }) {
  const { tenant } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Bumped after every change so the <img> refetches instead of showing the
  // browser's cached copy of the logo that was just replaced.
  const [version, setVersion] = useState(0);

  if (!tenant) return null;

  async function upload(file: File) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.uploadCompanyLogo(file);
      await onSaved();
      setVersion((n) => n + 1);
      setNotice('Logo saved. It will appear on forms submitted from now on.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not upload that logo.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.deleteCompanyLogo();
      await onSaved();
      setVersion((n) => n + 1);
      setNotice('Logo removed. Your company name appears in text instead.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove the logo.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>Logo</h2>
      {error && <Banner kind="error">{error}</Banner>}
      {notice && <Banner kind="ok">{notice}</Banner>}

      {tenant.hasLogo ? (
        <p>
          <img
            src={`/api/company/logo?v=${version}`}
            alt={`${tenant.name} logo`}
            style={{ maxWidth: '220px', maxHeight: '90px' }}
          />
        </p>
      ) : (
        <p>No logo. Your company name appears at the top of a form PDF in text.</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {/* Drop or browse (round 45), through the shared FileDrop's button
            shape; its hidden input keeps round 33's rule. The upload fires
            straight away, so there is no filename to show: the logo above IS
            the confirmation. */}
        <FileDrop
          variant="button"
          id="logo-file"
          accept={LOGO_ACCEPT}
          typeLabel="PNG or JPG"
          buttonClassName="btn-secondary"
          disabled={busy}
          label={busy ? 'Working…' : tenant.hasLogo ? 'Replace logo' : 'Upload a logo'}
          onFiles={(files) => {
            if (files[0]) void upload(files[0]);
          }}
          onReject={setError}
        />
        {tenant.hasLogo && (
          <button
            type="button"
            className="btn-secondary"
            disabled={busy}
            onClick={() => void remove()}
          >
            Remove
          </button>
        )}
      </div>

      <p className="hint">
        A PNG or a JPG, up to 2MB. Photos straight off an iPhone are usually HEIC files, which
        will not go into a PDF; export or screenshot one as a JPG first. A wide logo works
        better than a tall one.
      </p>
    </section>
  );
}
